/**
 * Builds the submission payload from form data, extracting only non-empty type-specific fields.
 */
export function buildInvestmentSubmitData(formData, isEditing) {
  const submitData = {
    name: formData.name,
    type: formData.type,
    amount: parseFloat(formData.amount),
    currency: formData.currency,
    institution: formData.institution || null,
    country: formData.country || null,
    current_value: formData.current_value ? parseFloat(formData.current_value) : null,
    purchase_date: formData.purchase_date ? new Date(formData.purchase_date).toISOString() : null,
    notes: formData.notes || null,
    owner_type: formData.owner_type,
    entity_id: formData.owner_type !== 'direct' && formData.entity_id ? formData.entity_id : null,
  };

  // String fields
  const STRING_FIELDS = [
    'ticker_symbol', 'stock_exchange', 'fund_manager', 'mutual_fund_type', 'risk_rating',
    'valuation_cap_type', 'wallet_address', 'issuer_name',
    'property_country', 'property_city', 'property_street_address', 'property_type',
    'etf_type', 'etf_isin', 'etf_index_tracked', 'etf_fund_manager', 'etf_fund_sponsor',
    'gold_purity_karats', 'gold_form', 'gold_storage_location',
  ];
  STRING_FIELDS.forEach(field => { if (formData[field]) submitData[field] = formData[field]; });

  // Float fields
  const FLOAT_FIELDS = [
    'interest_rate', 'conversion_ratio', 'discount_rate', 'valuation_cap', 'share_price',
    'property_purchase_price', 'property_current_market_price', 'property_mortgage_outstanding',
    'etf_units', 'etf_purchase_price_per_unit', 'etf_current_nav',
    'gold_weight_oz', 'gold_purchase_price_per_oz', 'gold_current_price_per_oz',
  ];
  FLOAT_FIELDS.forEach(field => { if (formData[field]) submitData[field] = parseFloat(formData[field]); });

  // Integer fields
  if (formData.number_of_shares) submitData.number_of_shares = parseInt(formData.number_of_shares);

  // Date fields
  const DATE_FIELDS = ['maturity_date', 'property_date_acquired'];
  DATE_FIELDS.forEach(field => { if (formData[field]) submitData[field] = new Date(formData[field]).toISOString(); });

  return submitData;
}

/**
 * Auto-calculate derived fields based on investment type and changed field.
 */
export function autoCalculateFields(prev, field, value) {
  const updated = { ...prev, [field]: value };

  // Private Equity: amount = shares * price
  if (prev.type === 'private_equity' && (field === 'number_of_shares' || field === 'share_price')) {
    const shares = parseFloat(field === 'number_of_shares' ? value : updated.number_of_shares) || 0;
    const price = parseFloat(field === 'share_price' ? value : updated.share_price) || 0;
    if (shares && price) updated.amount = (shares * price).toFixed(2);
  }

  // ETF: amount = units * purchase price, current_value = units * NAV
  if (prev.type === 'etf') {
    if (field === 'etf_units' || field === 'etf_purchase_price_per_unit') {
      const units = parseFloat(field === 'etf_units' ? value : updated.etf_units) || 0;
      const pp = parseFloat(field === 'etf_purchase_price_per_unit' ? value : updated.etf_purchase_price_per_unit) || 0;
      if (units && pp) updated.amount = (units * pp).toFixed(2);
    }
    if (field === 'etf_units' || field === 'etf_current_nav') {
      const units = parseFloat(field === 'etf_units' ? value : updated.etf_units) || 0;
      const nav = parseFloat(field === 'etf_current_nav' ? value : updated.etf_current_nav) || 0;
      if (units && nav) updated.current_value = (units * nav).toFixed(2);
    }
  }

  // Gold: amount = weight * purchase price, current_value = weight * current price
  if (prev.type === 'gold') {
    if (field === 'gold_weight_oz' || field === 'gold_purchase_price_per_oz') {
      const w = parseFloat(field === 'gold_weight_oz' ? value : updated.gold_weight_oz) || 0;
      const pp = parseFloat(field === 'gold_purchase_price_per_oz' ? value : updated.gold_purchase_price_per_oz) || 0;
      if (w && pp) updated.amount = (w * pp).toFixed(2);
    }
    if (field === 'gold_weight_oz' || field === 'gold_current_price_per_oz') {
      const w = parseFloat(field === 'gold_weight_oz' ? value : updated.gold_weight_oz) || 0;
      const cp = parseFloat(field === 'gold_current_price_per_oz' ? value : updated.gold_current_price_per_oz) || 0;
      if (w && cp) updated.current_value = (w * cp).toFixed(2);
    }
  }

  return updated;
}
